// What the conditions table actually says: the tabs, their rows, and each
// row's cells for the chosen columns.
//
// Data in, data out. This module reads the held forecast and the selected
// vessel+operation and returns a plain description of what to print; drawing it
// is table.ts's job. Keeping the two apart is what lets the whole row set be
// asserted in node without a canvas, and it is also why a units flip or a clock
// flip is a rebuild of this and nothing else.
//
// Two rules run through all of it:
//
//  - Never invent. A variable we do not fetch has no row, or has a row that
//    says it has no source (tides). A cell with no value prints an em dash and
//    takes no colour at all — absence is not a low number.
//  - Never re-derive. Limit fractions come from ops/workability.ts, sea
//    conditions from ops/briefing.ts, grades from WORK_GRADES. This file
//    arranges; it does not decide what is dangerous.

import type { PointForecast } from "../forecast/openmeteo.js";
import { firstWithData } from "../forecast/openmeteo.js";
import type { Solar } from "../forecast/solar.js";
import type { EffectiveLimit, Vessel } from "../ops/registry.js";
import {
  limitMargin,
  waveLimitFractionAt,
  windLimitFractionAt,
  workGrade,
  WORK_GRADES,
} from "../ops/workability.js";
import {
  crossSea,
  longSwell,
  rollResonance,
  slamRisk,
  windAgainstSwell,
} from "../ops/briefing.js";
import { convLen, convTemp, fmtLimitLen, lenUnit, tempUnit } from "../ui/units.js";

/** Which colour scale a row's cells are painted on. Stated on the panel, never
 *  inferred by the reader. */
export type CellScale = "limit" | "absolute" | "none";

export interface Cell {
  /** The printed number. "" when there is no value — never a zero. */
  text: string;
  /** The smaller second line (gusts). */
  sub?: string;
  /** The quantity's bearing in degrees true, quoted in ITS OWN convention —
   *  FROM for wind, wave and swell, TOWARD for a current — which the row
   *  declares once in `dirToward`. Always drawn as an arrow pointing where the
   *  flow is going. One shape for every quantity: the rows are labelled, so the
   *  label names the quantity and the glyph need not. */
  dirDeg?: number | null;
  /** 0..1 on the WORK_GRADES scale. Null on an ungraded cell. */
  p?: number | null;
  /** The raw value, for the absolute ramp. */
  value?: number | null;
  /** A derived sea-condition marker printed in the corner ("R", "X"…). */
  mark?: string;
  markTitle?: string;
  /** WORK_GRADES index a marker tints the cell to, when the cell has no grade
   *  of its own. Marks are conditions, not breaches, so they tint rather than
   *  grade — and the letter is always printed beside the tint. */
  markGrade?: number;
  /** Darkness level 0-3, for the daylight row. */
  night?: number;
}

export type RowKind = "value" | "daylight" | "tide";

export interface Row {
  label: string;
  /** The unit, printed under the label — "kn", "m"/"ft", "°C"/"°F". */
  unit: string;
  /** A second label line: the limit in force, the model, or a caveat. */
  note?: string;
  scale: CellScale;
  kind: RowKind;
  height: number;
  cells: Cell[];
  /** How this row's `dirDeg` is QUOTED. Omitted/false means FROM, which is every
   *  row here today — wind, wave and swell all publish a from-bearing. Set true
   *  for a quantity quoted TOWARD, which in this console means a current (SMOC
   *  set), so the arrow points at its own stated bearing instead of the opposite
   *  of one. The drawing itself never changes: see `glyphHeading`. */
  dirToward?: boolean;
  /** Absolute-ramp bounds, when scale is "absolute". */
  min?: number;
  max?: number;
}

export interface TabStatus {
  /** Worst WORK_GRADES index reached anywhere in the tab. */
  gradeIndex: number;
  label: string;
  /** Columns holding at least one No-go cell. Printed beside the colour, so
   *  the status is never carried by the colour alone. */
  noGo: number;
}

export interface Tab {
  id: string;
  label: string;
  rows: Row[];
  status: TabStatus | null;
  /** The footer line for this tab — its data sources, named. */
  source: string;
}

export interface TableInput {
  fc: PointForecast;
  solar: Solar | null;
  /** Indices into fc.times that became columns. */
  columns: readonly number[];
  vessel: Vessel | null;
  limit: EffectiveLimit | null;
  opLabel: string | null;
  /** The workability the Workability panel computed — read, never recomputed. */
  workP: readonly (number | null)[] | null;
}

// Row heights. `note` rows need two lines in the label column, so the scaffold
// row that carries only a caveat is not allowed to be the thinnest one — its
// whole job is that the caveat is readable.
const H = { wind: 34, tall: 28, normal: 25, thin: 18, note: 24 } as const;

const at = <T>(s: readonly (T | null)[] | null | undefined, i: number): T | null =>
  s == null ? null : s[i] ?? null;

const num = (v: number | null, digits = 0): string =>
  v === null || !Number.isFinite(v) ? "" : v.toFixed(digits);

/** The grade a 0..1 lands in, as an index into WORK_GRADES. */
export function gradeIndexOf(p: number): number {
  const g = workGrade(p);
  const i = WORK_GRADES.indexOf(g);
  return i < 0 ? WORK_GRADES.length - 1 : i;
}

/** The marker key printed under the table, so a letter in a cell is never a
 *  private code. Order is the order they are explained. */
export const MARKS: readonly { mark: string; meaning: string }[] = [
  { mark: "R", meaning: "wave period on the vessel's natural roll period — she will roll hard beam-on" },
  { mark: "r", meaning: "wave period near her roll period — lively rolling in beam seas" },
  { mark: "S", meaning: "steep, and deep enough against her draft to slam" },
  { mark: "s", meaning: "steep enough to risk slamming forward" },
  { mark: "X", meaning: "swell and wind sea crossed — a confused sea with no rhythm" },
  { mark: "x", meaning: "swell and wind sea crossed — an uneven, cross-running sea" },
  { mark: "W", meaning: "wind blowing against the swell — crests steepen and break early" },
  { mark: "L", meaning: "long swell — a slow, rolling motion" },
];

// --- Row builders ------------------------------------------------------------

function windRow(input: TableInput): Row {
  const { fc, columns, limit } = input;
  const model = fc.wind ? firstWithData(fc.wind.models) : null;
  const cells: Cell[] = columns.map((i) => {
    const v = model ? at(model.data, i) : null;
    const gust = at(fc.gustsKn, i);
    return {
      text: num(v),
      sub: gust === null ? undefined : `G${Math.round(gust)}`,
      dirDeg: at(fc.wind?.directionDeg ?? null, i),
      value: v,
      p: limit === null || v === null ? null : windLimitFractionAt(fc, i, limit.wind_kn),
    };
  });
  return {
    label: "Wind",
    unit: "kn",
    note:
      limit === null
        ? model?.label ?? undefined
        : `limit ${limit.wind_kn} kn${limit.isDefault ? " (default)" : ""}`,
    scale: limit === null ? "absolute" : "limit",
    kind: "value",
    height: H.wind,
    cells,
    min: 0,
    max: rampMax(cells, 20),
  };
}

function waveRow(input: TableInput): Row {
  const { fc, columns, limit } = input;
  const model = fc.waves ? firstWithData(fc.waves.height) : null;
  const cells: Cell[] = columns.map((i) => {
    const m = model ? at(model.data, i) : null;
    const cross = crossSea(
      at(fc.partition?.swellDir ?? null, i),
      at(fc.partition?.windSeaDir ?? null, i),
      at(fc.partition?.swellH ?? null, i),
      at(fc.partition?.windSeaH ?? null, i),
    );
    const opposed = windAgainstSwell(
      at(fc.wind?.directionDeg ?? null, i),
      at(fc.partition?.swellDir ?? null, i),
      model === null ? null : at(fc.wind?.models?.[0]?.data ?? null, i),
    );
    const cell: Cell = {
      text: m === null ? "" : convLen(m).toFixed(1),
      dirDeg: at(fc.waves?.directionDeg ?? null, i),
      value: m === null ? null : convLen(m),
      p: limit === null || m === null ? null : waveLimitFractionAt(fc, i, limit.wave_m),
    };
    if (cross !== null) {
      const confused = cross >= 90;
      cell.mark = confused ? "X" : "x";
      cell.markTitle = `swell and wind sea about ${Math.round(cross)}° apart`;
      cell.markGrade = confused ? 2 : 1;
    } else if (opposed) {
      cell.mark = "W";
      cell.markTitle = "wind blowing against the swell";
      cell.markGrade = 1;
    }
    return cell;
  });
  return {
    label: "Waves",
    unit: lenUnit(),
    note:
      limit === null
        ? model?.label ?? undefined
        : `limit ${fmtLimitLen(limit.wave_m)}${limit.isDefault ? " (default)" : ""}`,
    scale: limit === null ? "absolute" : "limit",
    kind: "value",
    height: H.tall,
    cells,
    min: 0,
    max: rampMax(cells, convLen(2)),
  };
}

/**
 * The wave models do not all publish the same period QUANTITY, and the console
 * must not let that read as model disagreement.
 *
 * ECMWF WAM (mwp), MFWAM (VTM10) and DWD GWAM (tm10) all report Tm−1,0, the
 * ENERGY period. GFS-Waves reports PERPW, the PEAK period — WMO's title for it,
 * "primary wave mean period", is a naming wart. In a bimodal sea the peak
 * period can read more than half again the energy period for the very same
 * water; in a unimodal sea they nearly agree.
 *
 * Two consequences:
 *
 *  1. The Models tab carries wind SPEED and wave HEIGHT rows only. Stacking
 *     four period rows for visual comparison would present a definition
 *     difference as disagreement, which is the single most misleading thing
 *     this table could show an oceanographer.
 *  2. The Conditions tab's one period row names which definition it is
 *     showing, because the roll-resonance marker compares that number against
 *     the hull's natural roll period — and peak-vs-energy is easily the
 *     difference between flagging resonance and not.
 *
 * The mapping itself has since moved to its proper home: WAVE_MODELS in
 * forecast/openmeteo.ts declares a required `periodKind` per model and composes
 * it into each period series' label. This file no longer keeps its own copy of
 * which model is which — it just prints the label it is handed, so the row note
 * and the chart's line names cannot drift apart.
 */

function periodRow(input: TableInput): Row {
  const { fc, columns, vessel } = input;
  const model = fc.waves ? firstWithData(fc.waves.period) : null;
  const heightModel = fc.waves ? firstWithData(fc.waves.height) : null;
  const rollT = vessel?.roll_period_s ?? null;
  const draft = vessel?.draft_m ?? null;
  const cells: Cell[] = columns.map((i) => {
    const p = model ? at(model.data, i) : null;
    const h = heightModel ? at(heightModel.data, i) : null;
    const cell: Cell = { text: num(p), value: p };
    const roll = rollResonance(p, rollT);
    const slam = slamRisk(h, p, fc.depthM, draft);
    // Resonance outranks slamming when both land: it is the sharper thing that
    // can be said about a hull, and one letter per cell is the budget.
    if (roll === "hard") {
      cell.mark = "R";
      cell.markTitle = `on ${vessel?.name.replace(/ —.*/, "") ?? "the vessel"}'s natural roll period (~${rollT} s)`;
      cell.markGrade = 3;
    } else if (roll === "lively") {
      cell.mark = "r";
      cell.markTitle = `near her natural roll period (~${rollT} s)`;
      cell.markGrade = 1;
    } else if (slam === "hard") {
      cell.mark = "S";
      cell.markTitle = "steep, and deep enough against her draft to slam";
      cell.markGrade = 3;
    } else if (slam === "warn") {
      cell.mark = "s";
      cell.markTitle = "steep enough to risk slamming forward";
      cell.markGrade = 2;
    }
    return cell;
  });
  // `model.label` already ends in "peak period" / "energy period" — composed in
  // openmeteo.ts beside the table that knows which. Printed as handed.
  const def = model === null ? "" : model.label;
  return {
    label: "Wave period",
    unit: "s",
    // Always name the model AND which period it publishes. "12 s" means two
    // different things depending on that, and the resonance marker below reads
    // this number.
    note: rollT === null ? def || undefined : `${def} · roll ≈${rollT} s`,
    scale: "absolute",
    kind: "value",
    height: H.normal,
    cells,
    min: rampMin(cells, 4),
    max: rampMax(cells, 12),
  };
}

function swellHeightRow(input: TableInput): Row {
  const { fc, columns } = input;
  const cells: Cell[] = columns.map((i) => {
    const v = at(fc.partition?.swellH ?? null, i);
    return {
      text: v === null ? "" : convLen(v).toFixed(1),
      dirDeg: at(fc.partition?.swellDir ?? null, i),
      value: v === null ? null : convLen(v),
    };
  });
  return {
    label: "Swell",
    unit: lenUnit(),
    scale: "absolute",
    kind: "value",
    height: H.tall,
    cells,
    min: 0,
    max: rampMax(cells, convLen(1.5)),
  };
}

function swellPeriodRow(input: TableInput): Row {
  const { fc, columns } = input;
  const cells: Cell[] = columns.map((i) => {
    const v = at(fc.partition?.swellPer ?? null, i);
    const cell: Cell = { text: num(v), value: v };
    if (longSwell(v)) {
      cell.mark = "L";
      cell.markTitle = "long swell — distant-swell energy, a slow rolling motion";
      cell.markGrade = 1;
    }
    return cell;
  });
  return {
    label: "Swell period",
    unit: "s",
    scale: "absolute",
    kind: "value",
    height: H.normal,
    cells,
    min: rampMin(cells, 4),
    max: rampMax(cells, 12),
  };
}

function sstRow(input: TableInput): Row {
  const { fc, columns } = input;
  const cells: Cell[] = columns.map((i) => {
    const v = at(fc.sstC, i);
    return { text: v === null ? "" : convTemp(v).toFixed(1), value: v === null ? null : convTemp(v) };
  });
  return {
    label: "Sea temp",
    unit: tempUnit(),
    scale: "absolute",
    kind: "value",
    height: H.normal,
    cells,
    min: rampMin(cells, 0),
    max: rampMax(cells, 1),
  };
}

function daylightRow(input: TableInput): Row {
  const { solar, columns } = input;
  const WORDS = ["day", "civil", "naut", "night"] as const;
  const cells: Cell[] = columns.map((i) => {
    const lvl = solar?.level?.[i];
    return lvl === undefined
      ? { text: "" }
      : { text: WORDS[lvl] ?? "", night: lvl };
  });
  return {
    label: "Daylight",
    unit: "",
    scale: "none",
    kind: "daylight",
    height: H.thin,
    cells,
  };
}

/**
 * The tide row.
 *
 * We have no tide time series. The port cards carry a static range, which is a
 * different thing entirely — a spring/neap span for a harbour, not a curve for
 * this point at this hour. So the row exists, says what is missing, and prints
 * nothing. Deleting it would be tidier and would also quietly answer "does this
 * console do tides?" with silence; an operator planning a nearshore CPT push
 * deserves to be told no rather than left to assume.
 */
function tideRow(input: TableInput): Row {
  return {
    label: "Tide",
    unit: "",
    note: "no tide source for this point",
    scale: "none",
    kind: "tide",
    height: H.note,
    cells: input.columns.map(() => ({ text: "" })),
  };
}

// --- Models tab --------------------------------------------------------------

function modelRows(input: TableInput): Row[] {
  const { fc, columns, limit } = input;
  const rows: Row[] = [];
  for (const m of fc.wind?.models ?? []) {
    const cells: Cell[] = columns.map((i) => {
      const v = at(m.data, i);
      return {
        text: num(v),
        value: v,
        p: limit === null || v === null ? null : limitMargin(v, limit.wind_kn),
      };
    });
    rows.push({
      label: m.label,
      unit: "kn",
      note: "wind",
      scale: limit === null ? "absolute" : "limit",
      kind: "value",
      height: H.normal,
      cells,
      min: 0,
      max: rampMax(cells, 20),
    });
  }
  for (const m of fc.waves?.height ?? []) {
    const cells: Cell[] = columns.map((i) => {
      const v = at(m.data, i);
      return {
        text: v === null ? "" : convLen(v).toFixed(1),
        value: v === null ? null : convLen(v),
        p: limit === null || v === null ? null : limitMargin(v, limit.wave_m),
      };
    });
    rows.push({
      label: m.label,
      unit: lenUnit(),
      note: "waves",
      scale: limit === null ? "absolute" : "limit",
      kind: "value",
      height: H.normal,
      cells,
      min: 0,
      max: rampMax(cells, convLen(2)),
    });
  }
  return rows;
}

// --- Workability tab ---------------------------------------------------------

function workabilityRows(input: TableInput): Row[] {
  const { fc, columns, limit, workP, opLabel, vessel } = input;
  const rows: Row[] = [];

  const verdict: Cell[] = columns.map((i) => {
    const p = at(workP ?? null, i);
    return {
      text: p === null ? "" : String(Math.round(p * 100)),
      sub: p === null ? undefined : workGrade(p).label,
      value: p,
      p,
    };
  });
  rows.push({
    label: "Workability",
    unit: "%",
    note:
      limit === null
        ? "pick a vessel and an operation"
        : `${vessel?.name.replace(/ —.*/, "") ?? "vessel"} · ${opLabel ?? "operation"}`,
    scale: "limit",
    kind: "value",
    height: H.wind,
    cells: verdict,
    min: 0,
    max: 1,
  });

  if (limit !== null) {
    rows.push({
      label: "Wind vs limit",
      unit: "%",
      note: `ensemble ≤ ${limit.wind_kn} kn`,
      scale: "limit",
      kind: "value",
      height: H.normal,
      cells: columns.map((i) => {
        const f = windLimitFractionAt(fc, i, limit.wind_kn);
        return { text: f === null ? "" : String(Math.round(f * 100)), p: f, value: f };
      }),
      min: 0,
      max: 1,
    });
    rows.push({
      label: "Waves vs limit",
      unit: "%",
      note: `models ≤ ${fmtLimitLen(limit.wave_m)}`,
      scale: "limit",
      kind: "value",
      height: H.normal,
      cells: columns.map((i) => {
        const f = waveLimitFractionAt(fc, i, limit.wave_m);
        return { text: f === null ? "" : String(Math.round(f * 100)), p: f, value: f };
      }),
      min: 0,
      max: 1,
    });
  }

  const SQ = ["", "possible", "likely", "severe"] as const;
  rows.push({
    label: "Squalls",
    unit: "",
    note: limit === null ? undefined : `${limit.squall} sensitivity`,
    scale: "none",
    kind: "value",
    height: H.normal,
    cells: columns.map((i) => {
      const lvl = fc.squall?.level[i] ?? 0;
      if (lvl === 0) return { text: "" };
      return {
        text: SQ[lvl] ?? "",
        mark: "⚡",
        markTitle: fc.squall?.why[i] ?? undefined,
        markGrade: lvl >= 3 ? 3 : lvl >= 2 ? 2 : 1,
      };
    }),
  });

  return rows;
}

// --- Ramp bounds -------------------------------------------------------------

function rampMax(cells: readonly Cell[], floor: number): number {
  let max = floor;
  for (const c of cells) {
    const v = c.value;
    if (v != null && Number.isFinite(v) && v > max) max = v;
  }
  return max;
}

function rampMin(cells: readonly Cell[], ceiling: number): number {
  let min = ceiling;
  for (const c of cells) {
    const v = c.value;
    if (v != null && Number.isFinite(v) && v < min) min = v;
  }
  return min;
}

// --- Tab status --------------------------------------------------------------

/**
 * The worst thing inside a tab, so the header can say where the trouble lives
 * before the tab is opened.
 *
 * Graded cells only. An absolute-scale row has no verdict to contribute — a
 * 14-second swell period is not "bad", it is long — and folding one in would
 * make the badge mean nothing. When a tab holds no graded cell at all the
 * status is null and the header shows no badge, which is the honest reading of
 * "nothing here is measured against a limit".
 *
 * The count is of COLUMNS, not cells: three no-go columns is a fact an operator
 * can act on, whereas six no-go cells could be three columns of two rows each
 * and reads as twice the trouble.
 */
export function tabStatus(rows: readonly Row[]): TabStatus | null {
  let worst = -1;
  const noGoColumns = new Set<number>();
  const lastIndex = WORK_GRADES.length - 1;
  for (const row of rows) {
    if (row.scale !== "limit") continue;
    row.cells.forEach((cell, c) => {
      const p = cell.p;
      if (p == null || !Number.isFinite(p)) return;
      const gi = gradeIndexOf(p);
      if (gi > worst) worst = gi;
      if (gi === lastIndex) noGoColumns.add(c);
    });
  }
  if (worst < 0) return null;
  return { gradeIndex: worst, label: WORK_GRADES[worst].label, noGo: noGoColumns.size };
}

// --- The tab set -------------------------------------------------------------

export function buildTabs(input: TableInput): Tab[] {
  const conditions: Row[] = [
    windRow(input),
    waveRow(input),
    periodRow(input),
    swellHeightRow(input),
    swellPeriodRow(input),
    sstRow(input),
    daylightRow(input),
    tideRow(input),
  ];
  const models = modelRows(input);
  const work = workabilityRows(input);

  return [
    {
      id: "conditions",
      label: "Conditions",
      rows: conditions,
      status: tabStatus(conditions),
      source:
        "Open-Meteo — wind from the GFS blend, waves and the swell partition from the marine models, sea temperature from the best-match marine run. Daylight computed from solar geometry." +
        " Wave period is whichever quantity the reporting model publishes: GFS-Waves reports PEAK period, while ECMWF WAM, MFWAM and DWD GWAM report ENERGY period (Tm−1,0). In a bimodal sea those differ by half again for the same water, so the row names which one it is showing.",
    },
    {
      id: "models",
      label: "Models",
      rows: models,
      status: tabStatus(models),
      source:
        "Open-Meteo — every wind and wave model the console fetches, on one axis. Rows that disagree are the honest measure of how much to trust any of them." +
        " Wind SPEED and wave HEIGHT only: the models do not all publish the same period quantity (GFS-Waves reports peak period, the others energy period), so stacking period rows here would show a difference in definitions as if it were a difference of opinion.",
    },
    {
      id: "workability",
      label: "Workability",
      rows: work,
      status: tabStatus(work),
      source:
        "Computed here: the fraction of GEFS members within the wind limit × the fraction of wave models within the wave limit, capped in squall hours. The same computation the Workability track draws.",
    },
  ];
}
